import React from 'react';
import { Icon } from '@components/shared';
import { token } from '@atlaskit/tokens';
import { css } from '@emotion/react';

/**
 * Interface representing a single lesson item in the list
 * @interface LessonListItem
 * @property {string} title - The title of the lesson
 * @property {string} slug - Unique identifier for the lesson
 * @property {string} duration - Duration of the lesson (e.g., "3 min", "30 min")
 * @property {boolean} isCompleted - Whether the lesson has been completed
 */

export interface LessonListItem {
  title: string;
  slug: string;
  duration: string;
  isCompleted: boolean;
}

/**
 * Props for the LessonsList component
 * @interface LessonsListProps
 * @property {LessonItem[]} lessons - Array of lesson items to be displayed
 * @property {(slug: string) => void} onLessonClick - Callback function when a lesson is clicked
 * @property {boolean} [isStandalone=false] - Whether the list should be displayed as a standalone component with border
 * @property {boolean} [isMobile=false] - Whether the component is displayed on mobile device
 * @property {boolean} [isTablet=false] - Whether the component is displayed on tablet device
 */

export interface LessonsListProps {
  lessons: LessonListItem[];
  onLessonClick: (slug: string) => void;
  isStandalone?: boolean;
  isTablet?: boolean;
}

/**
 * LessonsList is a component that displays a list of lessons with their completion status and duration.
 * Each lesson is clickable and shows a checkmark when completed.
 *
 * @component
 * @param {LessonsListProps} props - The component props
 * @returns {React.ReactElement} A list of lessons with their details
 *
 * @example
 * <LessonsList
 *   lessons={lessons}
 *   onLessonClick={handleLessonClick}
 *   isStandalone={true}
 * />
 */

export const LessonsList: React.FC<LessonsListProps> = ({
  lessons,
  onLessonClick,
  isStandalone = false,
  isTablet = false,
}) => {
  return (
    <div
      css={[
        lessonsListStyles,
        isStandalone && lessonsListStandaloneStyles,
        isTablet && !isStandalone && css({ borderRadius: '0' }),
      ]}
    >
      {lessons?.map(({ slug, isCompleted, title, duration }, index) => (
        <div
          key={slug}
          data-testid={`lesson-list-item-${index}`}
          tabIndex={0}
          css={[
            lessonItemStyles,
            !isStandalone && !isTablet && index === lessons.length - 1 && lastLessonItemStyles,
            isTablet && !isStandalone && index === 0 && firstLessonItemTabletStyles,
            isTablet && !isStandalone && index === lessons.length - 1 && lastLessonItemTabletStyles,
          ]}
          onClick={() => onLessonClick(slug)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onLessonClick(slug);
            }
          }}
        >
          <div css={circleContainerStyles}>
            {isCompleted ? (
              <Icon size="small" type="check-circle" color={token('color.border.success')} />
            ) : (
              <div css={emptyCircleStyles} />
            )}
          </div>
          <span css={[textStyles, isCompleted && { color: token('color.text.success') }]}>{title}</span>
          <div css={durationBadgeStyles}>{duration}</div>
          {isStandalone && (
            <div css={chevronContainerStyles}>
              <Icon size="medium" type="chevron-right" color={token('color.text')} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const lessonsListStyles = css({
  backgroundColor: '#FFFFFF',
  maxWidth: '100%',
  borderRadius: '0 0 8px 8px',
  overflow: 'hidden',
});

const lessonsListStandaloneStyles = css({
  border: `1px solid ${token('color.border')}`,
  borderRadius: '8px',
});

const lessonItemStyles = css({
  display: 'flex',
  flexDirection: 'row',
  borderTop: `1px solid ${token('color.border')}`,
  padding: `${token('space.150')} ${token('space.200')}`,
  cursor: 'pointer',

  '&:hover': {
    backgroundColor: token('color.background.accent.gray.subtlest'),
  },

  '&:active': {
    backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
  },

  ':first-of-type': {
    borderTop: 'none',
  },
});

const circleContainerStyles = css({
  marginTop: '3px',
});

const emptyCircleStyles = css({
  width: '14px',
  height: '14px',
  borderRadius: '50%',
  border: `1.4px solid ${token('color.border')}`,
  padding: token('space.075'),
  margin: '1px',
  marginTop: token('space.025'),
});

const textStyles = css({
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '20px',
  fontFamily: 'Inter',
  padding: `${token('space.025')} ${token('space.100')}`,
  color: token('color.text'),
});

const durationBadgeStyles = css({
  marginTop: '1px',
  padding: `${token('space.025')} ${token('space.075')}`,
  borderRadius: '8px',
  backgroundColor: token('color.background.disabled'),
  textTransform: 'uppercase',
  fontSize: '12px',
  fontWeight: '400',
  lineHeight: '18px',
  marginLeft: 'auto',
  whiteSpace: 'nowrap',
  height: 'fit-content',
});

const lastLessonItemStyles = css({
  borderRadius: '0 0 8px 8px',
});

const firstLessonItemTabletStyles = css({
  borderTop: `1px solid ${token('color.border')} !important`,
});

const lastLessonItemTabletStyles = css({
  borderRadius: '0 !important',
});

const chevronContainerStyles = css({
  display: 'none',

  '@media (max-width: 965px)': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '24px',
    marginLeft: token('space.050'),
    marginRight: token('space.negative.100'),
  },
});
